import GamedayAppContainer from './containers/GamedayAppContainer';
import datasourceManager from 'datasource-manager';
import DeviceInteracton from 'device-interaction';
import { default as dataRouter, routesConfig } from './store/DataRouter';
import customUrlBuilder, { customHashUrlBuilder } from './store/customUrlBuilder';
import HashState from './store/HashState';
import { resourcePath } from '@gameday/utils/resource-path';
import { autoGameStateSet, getGameTab, getGameDetail } from '@gameday/utils/parseShareUrl';
import { getFormattedLangOptions } from '@gameday/utils/languageHelpers';

class GamedayAppBase {
    constructor (_opts) {
        this.appContainer = GamedayAppContainer;
        const mergedOptions = {
            route: '/',
            tz: 'America/New_York',
            mount: '#bdata-react-component',
            ..._opts
        };

        const {
            mount,
            locale,
            language,
            game,
            timecode,
            config,
            tracking,
            detail,
            game_state,
            game_tab,
            basename,
            ads,
            useMemoryHistory
        } = mergedOptions;

        const isAutoGameState = !!autoGameStateSet.has(game_state);
        const isPlay = !!(game_state === 'play');
        const { updatedLanguage, updatedLocale } = getFormattedLangOptions(language, locale);
        const {
            env = 'prod',
            enableTabsFinal = {},
            enableTabsLive = {},
            enableSummaryFiltersLive = {},
            enableSummaryFiltersFinal = {},
            enableVideoTabAutoPlay = {},
            absConfig = {},
            appContext = undefined, // Explicitly undefined by default so the context can be malleable.
            resourcePathsOverride = undefined,
            hashState = {},
            language: configLang,
            locale: configLocale,
            appPath = '',
            ...restConfig
        } = config;

        const initHashState = {
            read: false,
            write: false,
            watch: false,
            separator: ',',
            ...hashState
        };

        if (initHashState.watch) {
            this.hashListener = new HashState({ game, timecode });
        }

        this.opts = {
            ..._opts,
            env,
            mount,
            routes: dataRouter,
            basename,
            customUrlBuilder: initHashState.write ? customHashUrlBuilder : customUrlBuilder,
            useMemoryHistory: useMemoryHistory ?? initHashState.read,
            locale: updatedLocale,
            language: updatedLanguage,
            initState: {
                config: {
                    absConfig: {
                        chip: false,
                        pitch: true,
                        ...absConfig
                    },
                    appPath: appPath !== '' ? `/${appPath}` : '',
                    configGamePk: game,
                    env,
                    appContext,
                    hashState: initHashState,
                    /*  START: Centralize the fallback values we use for context inspection to the store.
                            These are to be used only if the stitch config call fails, and we need to choose an offline default.
                    */
                    failsafeAppContext: 'mlb',
                    failsafeGameContext: 'mlb',
                    failsafeDisablePlayerLinks: true,
                    failsafeHeadshotPath: '',
                    failsafeHeadshotType: 'spots',
                    /*  END: Centralize the fallback values we use for context inspection to the store */
                    summaryParallaxAdSlotIndex: 2,
                    enableTabsFinal: {
                        wrap: true,
                        summary: true,
                        box: true,
                        video: true,
                        ...enableTabsFinal
                    },
                    enableTabsLive: {
                        live: true,
                        summary: true,
                        box: true,
                        video: true,
                        ...enableTabsLive
                    },
                    enableSummaryFiltersLive: {
                        all: true,
                        keyMoments: true,
                        scoringPlays: true,
                        highlights: true,
                        homeRuns: true,
                        strikeouts: true,
                        hardHits: true,
                        highLeverage: false,
                        leadChange: true,
                        winProb: true,
                        ...enableSummaryFiltersLive
                    },
                    enableSummaryFiltersFinal: {
                        keyMoments: true,
                        scoringPlays: true,
                        all: true,
                        highlights: true,
                        homeRuns: true,
                        strikeouts: true,
                        hardHits: true,
                        highLeverage: false,
                        leadChange: true,
                        winProb: true,
                        ...enableSummaryFiltersFinal
                    },
                    tracking,
                    ads,
                    deviceInteraction: DeviceInteracton.create(config?.deviceInteractionConfig || config?.appView || 'desktop'),
                    enableFieldVision: false,
                    enableFieldVisionOD: false,
                    forceFieldVisionOn: false,
                    enableDarkMode: false,
                    themeMode: 'auto',
                    batEnabled: true,
                    disableWebSockets: true,
                    webSocketWindow: 18,
                    forceWebSockets: false,
                    noDelayWebSockets: false,
                    localTimezone: 'America/New_York',
                    nativeVideoPlayer: false,
                    disableDynamicFieldView: false,
                    disableDynamicFieldViewDesktop: false,
                    disableDynamicFieldViewMobile: false,
                    useFallbackTeamUrl: false,
                    DFVSyncThresholdAhead: 0,
                    DFVSyncThresholdBehind: 2,
                    forceDFV: false,
                    disableTicketLink: false,
                    enableVideoTabAutoPlay: {
                        final: false,
                        ...enableVideoTabAutoPlay
                    },
                    enablePOTG: false,
                    dfvPlay: {
                        disable: false,
                        pitchTiming: 8.5,
                        resultTiming: 12.5
                    },
                    locale: updatedLocale,
                    language: updatedLanguage,
                    blockLoginActivities: updatedLanguage === 'ja',
                    ...restConfig
                },
                views: {
                    gamePk: game,
                    env,
                    timecode,
                    detail: getGameDetail(game_state, game_tab, detail),
                    game_view: (!isAutoGameState) ? game_state : '',
                    game_tab: getGameTab(game_state, game_tab),
                    isPlay,
                    atBatIndex: (isPlay) ? game_tab : undefined
                }
            }
        };

        datasourceManager.setEnv(env);
        if (resourcePathsOverride) {
            resourcePath.set(resourcePathsOverride);
        }
    }

    static getRoutesConfig () {
        return routesConfig;
    }

    init (_root) {
        this.root = _root.app;
        this.store = _root.store;
        this.render = _root.render;
        this.prefetch = _root.prefetch;
        this.renderWithStyle = _root.renderWithStyle;
    }
};

export default GamedayAppBase;
